import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, LinearProgress, Stack, Typography,
} from '@mui/material';
import NfcOutlinedIcon from '@mui/icons-material/NfcOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../axiosConfig';

export default function RfidStatisticsDialog({ actionSx }) {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    let active = true;
    api.get('/api/settings/rfid')
      .then(({ data }) => { if (active) setEnabled(data?.statisticsEnabled === true); })
      .catch(() => { if (active) setEnabled(false); });
    return () => { active = false; requestId.current += 1; };
  }, []);

  const fetchCounts = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError('');
    setCounts(null);
    try {
      const { data } = await api.get('/api/equipment/statistics/rfid');
      if (currentRequest !== requestId.current) return;
      const values = [data?.totalItems, data?.confirmedItems, data?.unconfirmedItems];
      if (values.some(value => !Number.isSafeInteger(value) || value < 0) ||
          data.confirmedItems + data.unconfirmedItems !== data.totalItems) {
        throw new Error('Invalid RFID statistics response.');
      }
      setCounts(data);
    } catch (failure) {
      if (currentRequest !== requestId.current) return;
      const disabled = failure?.response?.data?.code === 'RFID_STATISTICS_DISABLED';
      if (disabled) setEnabled(false);
      setError(disabled
        ? 'RFID statistics have been disabled for this register.'
        : 'Could not load RFID statistics. Please try again.');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, []);

  const close = () => {
    requestId.current += 1;
    setOpen(false);
    setLoading(false);
    setCounts(null);
    setError('');
  };
  const percentage = counts?.totalItems
    ? Math.round((counts.confirmedItems / counts.totalItems) * 1000) / 10
    : 0;

  return <>
    {enabled && <Button variant="outlined" size="small" sx={actionSx}
      startIcon={<NfcOutlinedIcon fontSize="small" />}
      onClick={() => { setOpen(true); fetchCounts(); }}>
      RFID
    </Button>}
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="rfid-statistics-title">
      <DialogTitle id="rfid-statistics-title">RFID confirmation</DialogTitle>
      <DialogContent dividers>
        {loading && <Stack direction="row" alignItems="center" spacing={1} role="status">
          <CircularProgress size={20} /><Typography>Loading RFID statistics…</Typography>
        </Stack>}
        {error && <Alert severity="warning">{error}</Alert>}
        {counts && <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {[
              ['Total items', counts.totalItems],
              ['RFID confirmed', counts.confirmedItems],
              ['Not yet confirmed', counts.unconfirmedItems],
            ].map(([label, value]) => <Box key={label} sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="h4" component="p">{value.toLocaleString()}</Typography>
            </Box>)}
          </Stack>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>{percentage}% confirmed</Typography>
            <LinearProgress variant="determinate" value={percentage}
              aria-label="RFID confirmed" aria-valuetext={`${percentage}% confirmed`} />
          </Box>
        </Stack>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5 }}>
          Active items in this register. Confirmation is recorded after a successful NFC match or verified tag write.
          Imported identifiers alone are not confirmation. Offline confirmations appear after synchronization.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          A recorded confirmation does not prove that a tag is still attached or unchanged.
        </Typography>
      </DialogContent>
      <DialogActions>
        {enabled && <Button onClick={fetchCounts} disabled={loading} startIcon={<RefreshIcon />}>Refresh</Button>}
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  </>;
}
